import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import AuthRoutes from "Routes/Components/Auth";

import UpdateProject from "Pages/Common/Projects/Update";

import { HTTPErrorCallback } from "Helpers/Error";
import moment from "Helpers/MomentCompat";
import { showNotification } from "Helpers/Toast";

import UseAuth from "Hooks/UseAuth";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseServices from "Hooks/UseServices";

import Button from "Components/Global/Buttons";

import ApprovedMaterials from "../ApprovedMaterials";
import Conversations from "../Conversations";
import Counterparts from "../Counterparts";
import Files from "../Files";
import Gallery from "../Gallery";
import ProjectHeader from "../Header";
import Materials from "../Materials";
import Questions from "../Questions";
import Quotas from "../Quotas";
import Reports from "../Reports";
import Schedule from "../Schedule";
import SponsorOrders from "../SponsorOrders";
import Simplified from "./Simplified";
import * as S from "./styles";

// Pergunta "Você possui interesse nesse projeto?" — OCULTA a pedido do cliente
// (20/08/2026). Só a exibição saiu: a funcionalidade continua inteira e em uso —
// `POST /projects/update-project-has-interest`, o campo `has_interest`, a coluna
// "Interesse do Patrocinador" no detalhe do projeto (admin) e o filtro
// Selecionados/Não Selecionados/Em avaliação da aba "Seletivas" seguem
// funcionando. Para reexibir, basta voltar esta constante para `true`.
const SHOW_SPONSOR_INTEREST_QUESTION = false;

// Aba "Perguntas" OCULTA a pedido do cliente (D28, 29/08/2026): "voltaremos a
// exibir depois que for pedido". A funcionalidade continua inteira — para
// reexibir, volte esta constante para `true`.
const SHOW_QUESTIONS_TAB = false;

const Project = ({ id, setSelected, isSelective }) => {
  const { authorized, parsedJWT } = UseAuth();
  const { api } = UseServices();
  const { setLoading } = UseGlobalLoading();
  const navigate = useNavigate();

  const [view, setView] = useState("form");
  const [projectData, setProjectData] = useState(null);

  const [files, setFiles] = useState({});
  const [documents, setDocuments] = useState({});

  const downloadProjectPdf = async () => {
    setLoading(true);
    try {
      const response = await api.post("/projects/get-own-project-pdf", {
        project_id: id,
      });

      const { pdf_base64, filename } = response.data;

      const bytes = Uint8Array.from(atob(pdf_base64), (char) =>
        char.charCodeAt(0),
      );
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download =
        filename || `${projectData?.project_name || "projeto"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      HTTPErrorCallback(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authorized) {
      navigate(AuthRoutes.Dashboard.path);
    }
  }, [authorized]);

  useEffect(() => {
    getProject(id);
  }, [id]);

  const getProject = async (id) => {
    setLoading(true);
    try {
      const response = await api.post(`/projects/get-own-project-by-id`, {
        project_id: id,
      });

      const project = response.data;
      const srcData = {};

      for (const key in project) {
        if (key.includes("_at")) {
          project[key] = moment(project[key]).format("YYYY-MM-DD");
        }

        if (key.includes("src")) {
          srcData[key] = project[key];
        }
      }

      setProjectData({
        ...project,
        project_links: project.project_links?.join(", "),
        srcData,
      });

      setFiles(project.project_files);
      setDocuments(project.project_documents);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    }
  };

  const updateHasInterest = async (has_interest) => {
    try {
      await api.post("/projects/update-project-has-interest", {
        project_id: id,
        has_interest,
      });

      setProjectData({
        ...projectData,
        has_interest,
      });

      showNotification(
        "success",
        "Sucesso!",
        has_interest
          ? "Registramos seu interesse neste projeto!"
          : "Você não tem interesse neste projeto.",
      );
    } catch (error) {
      HTTPErrorCallback(error);
    }
  };

  // Resposta do proponente: autoriza (ou não) o patrocinador do vínculo a
  // avaliar o projeto. Move o status do vínculo (Validado / Não validado).
  const updateValidation = async (project_sponsor_id, proponent_validation) => {
    try {
      await api.post("/projects/update-project-validation", {
        project_sponsor_id,
        proponent_validation,
      });

      showNotification(
        "success",
        "Sucesso!",
        proponent_validation
          ? "Avaliação autorizada! O patrocinador poderá conhecer o projeto."
          : "Avaliação não autorizada.",
      );

      // Recarrega para a validação respondida sair da lista de pendentes.
      getProject(id);
    } catch (error) {
      HTTPErrorCallback(error);
    }
  };

  const switchForm = (view) => {
    switch (view) {
      case "form":
        return (
          <UpdateProject id={id} reload={getProject} project={projectData} />
        );
      case "files":
        return (
          <Files
            projectId={id}
            reload={getProject}
            files={files}
            documents={documents}
          />
        );
      case "gallery":
        return <Gallery projectId={id} />;
      case "counterparts":
        return <Counterparts projectId={id} />;
      case "quotas":
        return <Quotas projectId={id} />;
      case "conversations":
        return <Conversations projectId={id} />;
      case "schedule":
        // O Cronograma exibe também o PERÍODO DO PROJETO (03/09/2026), que não é
        // evento cadastrado: vem do formulário base e é a data principal.
        return <Schedule projectId={id} project={projectData} />;
      case "approved_materials":
        // Mesma aba, dois papéis (D15): o patrocinador LÊ os aprovados; o
        // proponente ENVIA. O `value` continua "approved_materials" de
        // propósito — é chave interna, e renomear quebraria deep link e teste
        // sem ganho nenhum.
        return is_sponsor ? (
          <ApprovedMaterials projectId={id} />
        ) : (
          <Materials projectId={id} project={projectData} />
        );
      case "reports":
        return <Reports projectId={id} />;
      case "sponsor_orders":
        return <SponsorOrders projectId={id} />;
      case "questions":
        return <Questions projectId={id} />;
      default:
        return (
          <UpdateProject id={id} reload={getProject} project={projectData} />
        );
    }
  };

  const is_sponsor = parsedJWT?.user?.is_sponsor;

  // Modo simplificado (definido pelo backend a partir do status do vínculo): o
  // proponente autorizou a avaliação, mas o projeto ainda não foi apresentado —
  // o patrocinador vê só o básico, sem abas, sem PDF e sem editar a imagem.
  const isSimplified = projectData?.view_mode === "simplified";

  // Patrocinadores aguardando a autorização do proponente (um por vínculo).
  const pendingValidations = projectData?.pending_validations || [];

  // O gate `status_id !== 1` saiu das abas (tasks/projetos_aportador, D14): o
  // status do projeto passou a ser calculado pelo período e "Cadastrado"
  // deixou de existir como estado que esconde funcionalidade.
  const tabs = [
    { text: "Informações do projeto", value: "form", show: true },
    { text: "Documentos", value: "files", show: true },
    // "Galeria" → "Álbum" (D18 da parte 2, 03/09/2026).
    { text: "Álbum", value: "gallery", show: true },
    { text: "Contrapartidas", value: "counterparts", show: true },
    {
      // "Valor Aportado" para os DOIS perfis desde 03/09/2026 (D5 da parte 2:
      // "remove o conceito de cota e introduz o Valor Aportado"). Até então o
      // proponente via "Cotas", porque a sessão era o plano de cotas dele.
      text: "Valor Aportado",
      value: "quotas",
      show: true,
    },
    {
      text: "Validações",
      value: "sponsor_orders",
      show: !is_sponsor,
    },
    { text: "Mensagens", value: "conversations", show: true },
    // Sessões do projeto APORTADO (A12/A13 — D9/D21): o patrocinador só
    // chega ao detalhe completo quando Aportado; o PROPONENTE as vê quando o
    // projeto tem vínculo Aportado (flag has_contributed_link do backend).
    {
      text: "Cronograma",
      value: "schedule",
      show: is_sponsor || !!projectData?.has_contributed_link,
    },
    {
      // "Materiais Aprovados" é o nome do que o PATROCINADOR recebe. Para o
      // proponente a sessão é de ENVIO, e o rótulo passou a dizer isso (D15).
      text: is_sponsor ? "Materiais Aprovados" : "Materiais",
      value: "approved_materials",
      show: is_sponsor || !!projectData?.has_contributed_link,
    },
    {
      text: "Relatórios",
      value: "reports",
      show: is_sponsor || !!projectData?.has_contributed_link,
    },
    { text: "Perguntas", value: "questions", show: SHOW_QUESTIONS_TAB },
  ];

  return (
    <S.Wrapper>
      <S.BackButton>
        <Button
          text="Voltar"
          type="button"
          onClick={() => {
            setSelected(null);
          }}
        />
      </S.BackButton>

      <ProjectHeader
        id={id}
        projectData={projectData}
        getProject={getProject}
        readOnly={isSimplified}
      />

      {!isSimplified ? (
        <S.DownloadButton>
          <Button
            type="button"
            outline
            text="Baixar PDF do Projeto"
            onClick={downloadProjectPdf}
          />
        </S.DownloadButton>
      ) : null}

      {/* Proponente: autoriza (ou não) cada patrocinador que quer avaliar o
          projeto. É o que o status "Em validação" do vínculo aguarda. */}
      {!is_sponsor && pendingValidations.length ? (
        <S.PendingValidations>
          <h3>Solicitações de avaliação</h3>
          {pendingValidations.map((pending) => (
            <S.PendingValidation key={pending.project_sponsor_id}>
              {/* Redação definida pelo cliente em 18/08/2026. O nome da
                  seletiva fica na frase porque o proponente pode ter vários
                  pedidos pendentes — sem ele, dois pedidos do mesmo
                  patrocinador em seletivas diferentes ficam idênticos na tela.
                  Vínculo sem seletiva omite o trecho. */}
              <span>
                A Equipe Sevenx gostaria de apresentar esse projeto para o
                patrocinador <strong>{pending.sponsor_name || "—"}</strong>
                {pending.select_project_name ? (
                  <>
                    {" "}
                    na seletiva <strong>{pending.select_project_name}</strong>
                  </>
                ) : null}
                . Você valida essa apresentação?
              </span>
              <S.Buttons>
                <Button
                  type="button"
                  variant="success"
                  text="Sim"
                  onClick={() => {
                    updateValidation(pending.project_sponsor_id, true);
                  }}
                />
                <Button
                  type="button"
                  variant="critical"
                  text="Não"
                  onClick={() => {
                    updateValidation(pending.project_sponsor_id, false);
                  }}
                />
              </S.Buttons>
            </S.PendingValidation>
          ))}
        </S.PendingValidations>
      ) : null}

      {SHOW_SPONSOR_INTEREST_QUESTION &&
      is_sponsor &&
      isSelective &&
      projectData?.can_answer_interest ? (
        <S.HasInterest>
          <span>Você possui interesse nesse projeto?</span>
          <S.Buttons>
            <Button
              type="button"
              variant="success"
              text="Sim"
              onClick={() => {
                updateHasInterest(true);
              }}
              disabled={projectData?.has_interest}
            />
            <Button
              type="button"
              variant="critical"
              text="Não"
              onClick={() => {
                updateHasInterest(false);
              }}
              disabled={projectData?.has_interest === false}
            />
          </S.Buttons>
        </S.HasInterest>
      ) : null}

      {!isSimplified ? (
        <S.Tabs>
          {tabs
            .filter((tab) => tab.show)
            .map((tab) => (
              <Button
                key={tab.value}
                outline={view === tab.value}
                text={tab.text}
                type="button"
                onClick={() => {
                  setView(tab.value);
                }}
              />
            ))}
        </S.Tabs>
      ) : null}

      {projectData ? (
        isSimplified ? (
          <Simplified projectData={projectData} />
        ) : (
          switchForm(view)
        )
      ) : null}
    </S.Wrapper>
  );
};

export default Project;
